<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { HIGHLIGHT_PLACEMENTS } from '../utils/imovelShowConstants';

defineProps({
  statusLabel: {
    type: String,
    default: '',
  },
  statusTone: {
    type: String,
    default: 'neutral',
  },
  scheduledForLabel: {
    type: String,
    default: null,
  },
  schedule: {
    type: Object,
    default: null,
  },
  providerName: {
    type: String,
    default: null,
  },
  placementLabel: {
    type: Function,
    required: true,
  },
  minDateTime: {
    type: String,
    default: '',
  },
  submitting: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
  isValid: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit']);

const highlightForm = defineModel('highlightForm', {
  type: Object,
  required: true,
});

const { t } = useI18n();
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard :title="t('PROPERTIES.HIGHLIGHT.TITLE')" density="cozy">
    <p class="text-[13px] leading-relaxed text-lais-text-body">
      {{ t('PROPERTIES.HIGHLIGHT.DESCRIPTION') }}
    </p>

    <div
      class="mt-4 flex flex-col gap-2 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3 text-[13px]"
    >
      <div class="flex items-center justify-between gap-3">
        <span class="text-lais-text-muted">{{
          t('PROPERTIES.HIGHLIGHT.CURRENT_STATUS')
        }}</span>
        <LaisStatusBadge :label="statusLabel" :tone="statusTone" dot />
      </div>
      <p
        v-if="scheduledForLabel"
        class="flex items-center justify-between gap-3"
      >
        <span class="text-lais-text-muted">{{
          t('PROPERTIES.HIGHLIGHT.SCHEDULED_FOR')
        }}</span>
        <span class="font-semibold text-lais-text">{{
          scheduledForLabel
        }}</span>
      </p>
      <p
        v-if="schedule?.placement"
        class="flex items-center justify-between gap-3"
      >
        <span class="text-lais-text-muted">{{
          t('PROPERTIES.HIGHLIGHT.PLACEMENT')
        }}</span>
        <span class="font-semibold text-lais-text">{{
          placementLabel(schedule.placement)
        }}</span>
      </p>
      <p v-if="providerName" class="flex items-center justify-between gap-3">
        <span class="text-lais-text-muted">{{
          t('PROPERTIES.HIGHLIGHT.PROVIDER')
        }}</span>
        <span class="break-all text-right font-semibold text-lais-text">
          {{ providerName }}
        </span>
      </p>
    </div>

    <div class="mt-4 grid gap-3">
      <div class="grid gap-1.5">
        <label
          for="highlight-scheduled-for"
          class="text-[12px] font-semibold text-lais-text"
        >
          {{ t('PROPERTIES.HIGHLIGHT.DATE_LABEL') }}
        </label>
        <input
          id="highlight-scheduled-for"
          v-model="highlightForm.scheduledFor"
          type="datetime-local"
          :min="minDateTime"
          :disabled="submitting"
          class="h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
        />
      </div>

      <div class="grid gap-1.5">
        <label
          for="highlight-placement"
          class="text-[12px] font-semibold text-lais-text"
        >
          {{ t('PROPERTIES.HIGHLIGHT.PLACEMENT_LABEL') }}
        </label>
        <select
          id="highlight-placement"
          v-model="highlightForm.placement"
          :disabled="submitting"
          class="h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
        >
          <option
            v-for="option in HIGHLIGHT_PLACEMENTS"
            :key="option.value"
            :value="option.value"
          >
            {{ placementLabel(option.value) }}
          </option>
        </select>
      </div>

      <div class="grid gap-1.5">
        <label
          for="highlight-notes"
          class="text-[12px] font-semibold text-lais-text"
        >
          {{ t('PROPERTIES.HIGHLIGHT.NOTES_LABEL') }}
        </label>
        <textarea
          id="highlight-notes"
          v-model="highlightForm.notes"
          rows="2"
          maxlength="280"
          :disabled="submitting"
          :placeholder="t('PROPERTIES.HIGHLIGHT.NOTES_PLACEHOLDER')"
          class="w-full rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-[13px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
        />
      </div>
    </div>

    <p
      v-if="errorMessage"
      class="mt-3 rounded-lais-sm border border-lais-danger/40 bg-lais-danger-50 px-3 py-2 text-[12px] leading-relaxed text-lais-danger"
    >
      {{ errorMessage }}
    </p>

    <LaisPrimaryButton
      class="mt-4 w-full"
      variant="primary"
      icon="i-lucide-sparkles"
      :disabled="!isValid || submitting"
      :is-loading="submitting"
      @click="emit('submit')"
    >
      {{ t('PROPERTIES.HIGHLIGHT.SUBMIT') }}
    </LaisPrimaryButton>
  </LaisCard>
</template>
